// ─── Game Constants ───────────────────────────────────────────────────────────

export const TILE_SIZE = 16;
export const CHARACTER_WIDTH = 16;
export const CHARACTER_HEIGHT = 32;
export const CANVAS_WIDTH = 800;
export const CANVAS_HEIGHT = 600;
export const PLAYER_SPEED = 2; // pixels per frame
export const TARGET_FPS = 60;
export const FRAME_TIME = 1000 / TARGET_FPS;

// World dimensions (in tiles)
export const WORLD_COLS = 80;
export const WORLD_ROWS = 60;
export const WORLD_WIDTH = WORLD_COLS * TILE_SIZE;
export const WORLD_HEIGHT = WORLD_ROWS * TILE_SIZE;

// Animation
export const WALK_ANIM_FRAMES = 4;
export const WALK_ANIM_SPEED = 8; // ticks per frame change

// Camera
export const CAMERA_LERP = 0.1;

// Interaction
export const INTERACTION_RANGE = TILE_SIZE * 1.5;

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum TileType {
  Empty = 0,
  Sand = 1,
  Ocean = 2,
  Boardwalk = 3,
  Grass = 4,
  BuildingFloor = 5,
  BuildingExterior = 6,
  Door = 7,
  Wall = 8,
  Pool = 9,
  PalmTrunk = 10,
  PalmLeaves = 11,
  TikiBar = 12,
  WaveAnimated = 13,
  Path = 14,
  FlowerPatch = 15,
  DarkGrass = 16,
  TreeCanopy = 17,
  Rock = 18,
}

export enum Direction {
  Up = 0,
  Down = 1,
  Left = 2,
  Right = 3,
}

export enum DepartmentType {
  Engineering = "engineering",
  Marketing = "marketing",
  Sales = "sales",
  Finance = "finance",
  HR = "hr",
  Operations = "operations",
  Design = "design",
  Support = "support",
}

// ─── Color Palette ────────────────────────────────────────────────────────────

export const TILE_COLORS: Record<TileType, string> = {
  [TileType.Empty]: "#1B2631",
  [TileType.Sand]: "#F4D03F",
  [TileType.Ocean]: "#2E86C1",
  [TileType.Boardwalk]: "#8B6914",
  [TileType.Grass]: "#27AE60",
  [TileType.BuildingFloor]: "#D5D8DC",
  [TileType.BuildingExterior]: "#566573",
  [TileType.Door]: "#4A235A",
  [TileType.Wall]: "#566573",
  [TileType.Pool]: "#85C1E9",
  [TileType.PalmTrunk]: "#6E2C00",
  [TileType.PalmLeaves]: "#1E8449",
  [TileType.TikiBar]: "#A04000",
  [TileType.WaveAnimated]: "#2471A3",
  [TileType.Path]: "#B0A090",
  [TileType.FlowerPatch]: "#4CAF50",
  [TileType.DarkGrass]: "#3A8B3A",
  [TileType.TreeCanopy]: "#2D6A1F",
  [TileType.Rock]: "#9E9E9E",
};

export const DEPARTMENT_COLORS: Record<string, string> = {
  [DepartmentType.Engineering]: "#2980B9",
  [DepartmentType.Marketing]: "#E74C3C",
  [DepartmentType.Sales]: "#F39C12",
  [DepartmentType.Finance]: "#27AE60",
  [DepartmentType.HR]: "#8E44AD",
  [DepartmentType.Operations]: "#E67E22",
  [DepartmentType.Design]: "#1ABC9C",
  [DepartmentType.Support]: "#3498DB",
};

// Lighter shades for interior floors
export const DEPARTMENT_FLOOR_COLORS: Record<string, string> = {
  [DepartmentType.Engineering]: "#AED6F1",
  [DepartmentType.Marketing]: "#F5B7B1",
  [DepartmentType.Sales]: "#FAD7A0",
  [DepartmentType.Finance]: "#A9DFBF",
  [DepartmentType.HR]: "#D2B4DE",
  [DepartmentType.Operations]: "#F0C8A0",
  [DepartmentType.Design]: "#A3E4D7",
  [DepartmentType.Support]: "#AED6F1",
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Position {
  x: number;
  y: number;
}

export interface GridPosition {
  col: number;
  row: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Interactable {
  id: string;
  type: "building" | "section" | "surggie" | "gem";
  position: Position;
  width: number;
  height: number;
  data?: Record<string, unknown>;
}

export interface RoomData {
  id: string;
  name: string;
  type: "overworld" | "department" | "section";
  tileMap: number[][];
  collisionMap: boolean[][];
  interactables: Interactable[];
  width: number; // in tiles
  height: number; // in tiles
}
